/**
 * Type definitions for Mermaid diagram parsing and generation
 */

import type { Edge, Node } from "@xyflow/react"

export type MermaidNodeShape =
  | "rectangle"
  | "rounded"
  | "stadium"
  | "subroutine"
  | "cylinder"
  | "circle"
  | "asymmetric"
  | "rhombus"
  | "hexagon"
  | "parallelogram"
  | "parallelogramAlt"
  | "trapezoid"
  | "trapezoidAlt"
  | "doubleCircle"

export type MermaidEdgeStyle = "solid" | "dotted" | "thick" | "invisible"

export type MermaidEdgeArrow = "arrow" | "open" | "circle" | "cross" | "none"

export type MermaidDirection = "TB" | "TD" | "BT" | "RL" | "LR"

export type MermaidNodeData = {
  label: string
  shape: MermaidNodeShape
  subgraph?: string
}

export type MermaidEdgeData = {
  label?: string
  style: MermaidEdgeStyle
  sourceArrow: MermaidEdgeArrow
  targetArrow: MermaidEdgeArrow
}

export type MermaidNode = Node<MermaidNodeData, "mermaid">

export type MermaidEdge = Edge<MermaidEdgeData>

export type ParsedMermaid = {
  direction: MermaidDirection
  nodes: MermaidNode[]
  edges: MermaidEdge[]
  subgraphs: Map<string, { id: string; label: string; nodeIds: string[] }>
}

export type ShapeSyntax = {
  open: string
  close: string
  shape: MermaidNodeShape
}

export const SHAPE_SYNTAXES: ShapeSyntax[] = [
  { open: "(((", close: ")))", shape: "doubleCircle" },
  { open: "((", close: "))", shape: "circle" },
  { open: "(", close: ")", shape: "rounded" },
  { open: "([", close: "])", shape: "stadium" },
  { open: "[[", close: "]]", shape: "subroutine" },
  { open: "[(", close: ")]", shape: "cylinder" },
  { open: "{", close: "}", shape: "rhombus" },
  { open: "{{", close: "}}", shape: "hexagon" },
  { open: "[/", close: "/]", shape: "parallelogram" },
  { open: "[\\", close: "\\]", shape: "parallelogramAlt" },
  { open: "[/", close: "\\]", shape: "trapezoid" },
  { open: "[\\", close: "/]", shape: "trapezoidAlt" },
  { open: ">", close: "]", shape: "asymmetric" },
  { open: "[", close: "]", shape: "rectangle" },
]

export type EdgeSyntax = {
  pattern: RegExp
  style: MermaidEdgeStyle
  sourceArrow: MermaidEdgeArrow
  targetArrow: MermaidEdgeArrow
}

export const EDGE_SYNTAXES: EdgeSyntax[] = [
  { pattern: /^<==>\s*/, style: "thick", sourceArrow: "arrow", targetArrow: "arrow" },
  { pattern: /^==>\s*/, style: "thick", sourceArrow: "none", targetArrow: "arrow" },
  { pattern: /^<==\s*/, style: "thick", sourceArrow: "arrow", targetArrow: "none" },
  { pattern: /^===\s*/, style: "thick", sourceArrow: "none", targetArrow: "none" },
  { pattern: /^<--!?>\s*/, style: "solid", sourceArrow: "arrow", targetArrow: "arrow" },
  { pattern: /^--!?>(?:\s*)/, style: "solid", sourceArrow: "none", targetArrow: "arrow" },
  { pattern: /^<--\s*/, style: "solid", sourceArrow: "arrow", targetArrow: "none" },
  { pattern: /^---\s*/, style: "solid", sourceArrow: "none", targetArrow: "none" },
  { pattern: /^<-\.->\s*/, style: "dotted", sourceArrow: "arrow", targetArrow: "arrow" },
  { pattern: /^-\.->\s*/, style: "dotted", sourceArrow: "none", targetArrow: "arrow" },
  { pattern: /^<-\.-\s*/, style: "dotted", sourceArrow: "arrow", targetArrow: "none" },
  { pattern: /^-\.-\s*/, style: "dotted", sourceArrow: "none", targetArrow: "none" },
  { pattern: /^~~~\s*/, style: "invisible", sourceArrow: "none", targetArrow: "none" },
]
